import { Component } from '@angular/core';
import { EntityFns } from '@data';
import { Entity, EntityError } from 'breeze-client';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

interface ErrorGroup {
  key: string;
  summary: string;
  errors: EntityError[];
  expanded: boolean;
}

@Component({
    selector: 'prox-validation-dialog',
    template: `
  <div class="h-full flex flex-col">
    <div class="text-l font-bold mb-3">{{ message}}</div>
    <div class="grow" style="overflow: auto">
      <ul *ngIf="!!groups">
        <ng-container *ngFor="let g of groups">
          <!-- Single-error group: render inline (no grouping chrome) -->
          <li *ngIf="g.errors.length === 1" class="my-2">
            <ng-container *ngIf="!data.showEntityTypes">
              <a (click)="onClick(g.errors[0], $event)">{{ g.errors[0].errorMessage }}</a>
            </ng-container>
            <div *ngIf="data.showEntityTypes" class="flex">
              <span class="w-1/3">{{ g.errors[0].entity['entityDisplayName'] }}</span>
              <a class="w-2/3" (click)="onClick(g.errors[0], $event)">{{ g.errors[0].errorMessage }}</a>
            </div>
          </li>

          <!-- Multi-error group: collapsible header + list -->
          <li *ngIf="g.errors.length > 1" class="my-2">
            <a (click)="toggle(g)" class="font-semibold flex items-center">
              <i class="pi mr-2" [ngClass]="g.expanded ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
              <span>{{ g.errors.length }} entries: {{ g.summary }}</span>
            </a>
            <ul *ngIf="g.expanded" class="ml-6 mt-2">
              <li *ngFor="let e of g.errors" class="my-1">
                <ng-container *ngIf="!data.showEntityTypes">
                  <a (click)="onClick(e, $event)">{{ e.errorMessage }}</a>
                </ng-container>
                <div *ngIf="data.showEntityTypes" class="flex">
                  <span class="w-1/3">{{ e.entity['entityDisplayName'] }}</span>
                  <a class="w-2/3" (click)="onClick(e, $event)">{{ e.errorMessage }}</a>
                </div>
              </li>
            </ul>
          </li>
        </ng-container>
      </ul>
    </div>
    <div class="text-right mt-3">
      <button (click)="revalidate()" pButton type="button" icon="pi pi-refresh" label="Revalidate"
            class="p-button-raised mr-2"></button>
      <button (click)="close()" pButton type="button" icon="pi pi-times" label="Close"
            class="p-button-raised"></button>
    </div>
  </div>
  `,
    standalone: false
})
export class ValidationDialogComponent {

  message?: string;
  groups: ErrorGroup[] = [];
  data: {
    parent: any | null,
    errEntities: Entity[],
    callback: (ee: EntityError, event: MouseEvent) => void,
    showEntityTypes: boolean,
    // When true (Program save path), repeated errors are collapsed into expandable
    // groups so a wall of identical messages doesn't drown out the rule explanation.
    // Default false — small forms (e.g. Account User) keep each error on its own row.
    groupRepeated?: boolean,
  };
  constructor(dialogConfig: DynamicDialogConfig, private ref: DynamicDialogRef) {
    this.data = dialogConfig.data;
    this.rebuildGroups(EntityFns.makeEntityErrors(this.data.errEntities));
    this.message = "Please correct the following validation errors.  Click on each error to go to the correct part of the form."
  }

  onClick(ee: EntityError, event: MouseEvent) {
    this.data.callback(ee, event);
  }

  toggle(g: ErrorGroup) {
    g.expanded = !g.expanded;
  }

  async revalidate() {
    const parent = this.data.parent;
    let errEntities: Entity[];
    if (parent && 'validateEntities' in parent) {
      errEntities = await parent.validateEntities();
    } else {
      errEntities = this.data.errEntities.filter(e => !e.entityAspect.validateEntity());
    }
    if (errEntities.length == 0) {
      this.close();
    }
    this.rebuildGroups(EntityFns.makeEntityErrors(errEntities));
  }

  close() {
    this.ref.close();
  }

  /** Group errors by rule, preserving original order of first occurrence.
   *  Groups of size 1 are kept as-is; groups of size >= 2 get a derived summary
   *  so the header reads as one sentence regardless of how many rows it covers.
   *
   *  Group key, in priority order:
   *    1) The save-mixin rule code embedded in the message as "(PFC-XXX)".
   *       This is needed because save-mixin errors (createValidationError) get
   *       unique per-call keys ('saveMixinError-N'), so errorName cannot group
   *       them on its own.
   *    2) errorName (validator.name || verr.key) — works for normal Breeze
   *       validators (e.g., 'required', 'maxLength').
   *    3) propertyName — last-resort fallback. */
  private rebuildGroups(errors: EntityError[]) {
    // Default behavior: one row per error, no grouping. Only Program saves opt into
    // the collapse-by-rule treatment, because they routinely produce dozens of
    // repeated messages (same rule against every PUG); small forms read better flat.
    if (!this.data.groupRepeated) {
      this.groups = errors.map((e, ix) => ({
        key: 'flat:' + ix,
        errors: [e],
        summary: e.errorMessage,
        expanded: false,
      }));
      return;
    }

    const order: string[] = [];
    const byKey = new Map<string, EntityError[]>();
    for (const e of errors) {
      const key = this.groupKey(e);
      if (!byKey.has(key)) {
        byKey.set(key, []);
        order.push(key);
      }
      byKey.get(key)!.push(e);
    }
    this.groups = order.map(key => {
      const list = byKey.get(key)!;
      return {
        key,
        errors: list,
        summary: list.length > 1 ? this.deriveSummary(list) : list[0].errorMessage,
        expanded: false,
      };
    });
  }

  /** Extract a stable group key. See rebuildGroups for the priority chain. */
  private groupKey(e: EntityError): string {
    const codeMatch = (e.errorMessage || '').match(/\(([A-Z]+-[A-Za-z0-9]+)\)\s*$/);
    if (codeMatch) return 'code:' + codeMatch[1];
    if (e.errorName && !/^saveMixinError-/.test(e.errorName)) return 'name:' + e.errorName;
    if (e.propertyName) return 'prop:' + e.propertyName;
    return 'other:' + (e.errorName || '');
  }

  /** Derive a single readable summary from a group of similar messages by
   *  keeping the common prefix and suffix and replacing the per-row variable
   *  middle with an ellipsis. If the common text is too short to be useful,
   *  fall back to the first message verbatim. */
  private deriveSummary(errors: EntityError[]): string {
    const messages = errors.map(e => e.errorMessage || '');
    if (messages.length === 0) return '';
    if (messages.length === 1) return messages[0];

    const prefix = this.commonPrefix(messages);
    const suffix = this.commonSuffix(messages, prefix.length);
    const first = messages[0];
    const middle = first.substring(prefix.length, first.length - suffix.length);

    // If the common stem is too short, the messages don't really share a
    // template; show the first verbatim rather than a misleading summary.
    const stemLength = prefix.length + suffix.length;
    if (stemLength < 20 || stemLength < first.length * 0.3) {
      return first;
    }

    // If the variable middle is empty, the messages were identical.
    if (!middle.trim()) {
      return (prefix + suffix).trim();
    }

    return (prefix + '…' + suffix).replace(/\s+/g, ' ').trim();
  }

  private commonPrefix(strs: string[]): string {
    if (strs.length === 0) return '';
    let i = 0;
    const first = strs[0];
    while (i < first.length && strs.every(s => s.charCodeAt(i) === first.charCodeAt(i))) {
      i++;
    }
    return first.substring(0, i);
  }

  private commonSuffix(strs: string[], minRemainingFromEnd: number): string {
    if (strs.length === 0) return '';
    const first = strs[0];
    const maxLen = Math.min(...strs.map(s => s.length)) - minRemainingFromEnd;
    if (maxLen <= 0) return '';
    let i = 0;
    while (i < maxLen
      && strs.every(s => s.charCodeAt(s.length - 1 - i) === first.charCodeAt(first.length - 1 - i))) {
      i++;
    }
    return first.substring(first.length - i);
  }

}
